import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';

import { copyColumnToClipboard } from '../lib/copy-column';

type CopyColumnButtonProps<T> = {
  columnKey: keyof T;
  data: T[];
  /** Готовые значения столбца — когда в ячейке не сырое поле строки. */
  values?: (string | number)[];
};

export function CopyColumnButton<T>({
  columnKey,
  data,
  values,
}: CopyColumnButtonProps<T>): JSX.Element {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  async function handleCopy(): Promise<void> {
    try {
      if (values) await navigator.clipboard.writeText(values.join('\n'));
      else await copyColumnToClipboard<T>(columnKey, data);
      setCopied(true);
      toast.success('Столбец скопирован');
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Не удалось скопировать');
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-5 ml-1 opacity-50 hover:opacity-100 shrink-0"
          onClick={handleCopy}
        >
          {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent>Скопировать столбец</TooltipContent>
    </Tooltip>
  );
}
